import React, { useEffect, useState } from "react";
import { Form, Input } from "antd";
import { EComModal } from "components/EComModal";
import { message } from "components";
import { RELEASE_NOTES_MAX_LENGTH } from "constants/aiccLanguage";
import { AiccManagedLanguage } from "types/model/aiccLanguage";
import * as aiccLanguageAPI from "services/aiccLanguage";
import { publishOkText, publishTitle, renderPublishIntro } from "../configs";
import { liveVersionNumber, workingCopyVersionNumber } from "../languageStatus";
import styles from "./Dialogs.module.scss";

interface Props {
  /** The language being published; absent keeps the modal closed. */
  language?: AiccManagedLanguage;
  onCancel: () => void;
  /** Handed the language's id, so the caller can re-read that row. */
  onPublished: (languageId: number) => void;
}

function PublishModal({ language, onCancel, onPublished }: Props) {
  const [form] = Form.useForm();
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!language) {
      form.resetFields();
    }
  }, [form, language]);

  const handleOk = () => {
    if (!language) {
      return;
    }
    form.validateFields().then(({ releaseNotes }) => {
      setIsSaving(true);
      aiccLanguageAPI
        .publishAiccLanguage(language.id, releaseNotes)
        .then(() => {
          message.success(`${language.name} is now public to all users.`);
          onPublished(language.id);
          onCancel();
        })
        .finally(() => setIsSaving(false));
    });
  };

  // Pre-defined rather than editable: the number follows from the version history, and only
  // a publish advances it.
  const version = language ? workingCopyVersionNumber(language) : 1;
  const liveNumber = language ? liveVersionNumber(language) : undefined;

  return (
    <EComModal
      visible={!!language}
      // Two shapes: the first publish makes a language available, a later one replaces the
      // version every end user is on, and AICC2-331 AC-4 wants that spelled out.
      title={publishTitle(language?.name ?? "", liveNumber)}
      okText={publishOkText(liveNumber)}
      onOk={handleOk}
      onCancel={onCancel}
      confirmLoading={isSaving}
    >
      <div className={styles.publishIntro}>{renderPublishIntro(language?.name ?? "", liveNumber, version)}</div>
      <div className={styles.publishIntro}>Please provide version control notes before proceeding.</div>

      <div className={styles.publishSectionTitle}>Version Control Notes (Required)</div>

      <Form form={form} layout="vertical">
        <Form.Item label="Version">
          <span className={styles.publishVersion}>{`V${version}`}</span>
        </Form.Item>
        <Form.Item
          label="Release Notes"
          name="releaseNotes"
          rules={[
            { required: true, message: "Please provide release notes." },
            {
              max: RELEASE_NOTES_MAX_LENGTH,
              message: `Release notes cannot exceed ${RELEASE_NOTES_MAX_LENGTH} characters.`,
            },
          ]}
        >
          <Input.TextArea rows={4} maxLength={RELEASE_NOTES_MAX_LENGTH} showCount />
        </Form.Item>
      </Form>
    </EComModal>
  );
}

export default PublishModal;
